import { inject, Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { DeviceDimensionsService } from '@easy-lib/modules/shared/services/device-dimensions.service';
import { DeviceTypeEnum } from '@easy-lib/modules/account-activity/models/accountDto';
import { ANDROID, DESKTOP, IOS, OTHER_PLATFORMS, VALID_BROWSER_VERSIONS } from '@easy-lib/constants/general.constant';
import { UserDeviceInfo } from '@easy-lib/modules/symbol-details/models/userDeviceInfo.model';
import { BaseService } from '@easy-lib/modules/shared/services/Base/base.service';
import { keyboardTiming } from '@easy-lib/constants/timing.constants';
import { MobileRoutes } from '@easy-lib/constants/routing.constant';

@Injectable({
	providedIn: 'root'
})
export class UserDeviceService extends BaseService {
	private dimensionsService: DeviceDimensionsService = inject(DeviceDimensionsService);
	private deviceService: DeviceDetectorService = inject(DeviceDetectorService);
	public isIOSSub = new BehaviorSubject<boolean>(undefined);
	public isIOSObs$ = this.isIOSSub.asObservable().pipe(filter(value => value !== undefined));

	get url(): string {
		return `${this.mtsPath + this.apiUrls.easy}UserDevice`;
	}

	delayToRemoveKeyboardAfterClosing?: number; //200
	delayToActiveKeyboardAfterOpening?: number; //100

	deviceType?: DeviceTypeEnum;
	UserDeviceInfoSubject = new BehaviorSubject<UserDeviceInfo>(null);
	UserDeviceInfo$ = this.UserDeviceInfoSubject.asObservable();

	constructor() {
		super();
		this.setKeyboardTiming();

		this.isIOSSub.next(/iOS/.test(this.deviceService.os));
	}

	get browser(): string {
		return this.deviceService.browser.toLowerCase();
	}

	get browserVersion(): string {
		return (
			this.deviceService.browser_version.toLowerCase().split('.')?.[0] +
			'.' +
			(this.deviceService.browser_version.toLowerCase().split('.')?.[1] ?? 0)
		);
	}

	get userDeviceInfo() {
		return this.deviceService;
	}

	isValidBrowserVersion(deviceType: DeviceTypeEnum): boolean {
		if (deviceType !== DeviceTypeEnum.Unknown) {
			for (const key of VALID_BROWSER_VERSIONS.keys()) {
				if (this.browser.includes(key) || key.includes(this.browser)) {
					return VALID_BROWSER_VERSIONS.get(key).isValid(
						deviceType === DeviceTypeEnum.Mobile,
						this.browserVersion
					);
				}
			}
		}
		return true;
	}

	get isAppropriateBrowserForAndroid(): boolean {
		return this.deviceService.browser.toLowerCase() === 'chrome';
	}

	get userDevice(): string {
		if (/Android/i.test(this.deviceService?.os)) {
			return ANDROID;
		}
		if (/iOS/.test(this.deviceService?.os)) {
			return IOS;
		}
		return OTHER_PLATFORMS;
	}

	get isiOS(): boolean {
		return this.userDevice === IOS;
	}

	get isAndroid(): boolean {
		return this.userDevice === ANDROID;
	}

	get isiOS_13_Or_Above(): boolean {
		return this.isiOS && /OS 13|OS 14|OS 15|OS 16|OS 17|OS 18|OS 19|OS 20/.test(this.deviceService.userAgent);
	}

	// checkIfqueuemicrotaskSupport(): boolean {
	// 	return typeof queueMicrotask === 'function';
	// }

	get is_iOS_Older_Version(): boolean {
		const userAgent = navigator.userAgent || navigator.vendor;
		return this.isiOS && /OS 10|OS 11|OS 12|OS 13|OS 14/.test(userAgent);
	}

	setKeyboardTiming() {
		if (this.userDevice === ANDROID) {
			this.delayToActiveKeyboardAfterOpening = keyboardTiming.delayToActiveAfterOpen;
			this.delayToRemoveKeyboardAfterClosing = keyboardTiming.delayToRemoveAfterClose;
		} else {
			this.delayToActiveKeyboardAfterOpening = keyboardTiming.delayToActiveAfterOpen; //0;
			this.delayToRemoveKeyboardAfterClosing = keyboardTiming.delayToRemoveAfterClose; //0;
		}
	}

	sendUserDeviceInfo() {
		const model = <UserDeviceInfo>{
			appBuildNo: this.environment.build,
			width: this.dimensionsService.width,
			height: this.dimensionsService.height,
			devicePlatform: this.userDevice ?? 'Mobile',
			platformInfo: this.deviceService.userAgent
		};
		this.UserDeviceInfoSubject.next(model);
	}

	sendUserDesktopDeviceInfo() {
		const model = <UserDeviceInfo>{
			appBuildNo: this.environment.build,
			width: this.dimensionsService.width,
			height: this.dimensionsService.height,
			devicePlatform: DESKTOP,
			platformInfo: this.deviceService.userAgent
		};
		this.UserDeviceInfoSubject.next(model);
		// this.postUserDeviceInfo(model).subscribe();
	}

	deviceTypeSub = new BehaviorSubject<DeviceTypeEnum>(DeviceTypeEnum.Unknown);
	deviceType$ = this.deviceTypeSub.asObservable();

	setUserDevice(deviceType: DeviceTypeEnum) {
		this.deviceType = deviceType;
		this.deviceTypeSub.next(deviceType);
	}

	public checkInfrastructure() {
		this.httpService.get<any>('https://api-mts.orbis.easytrader.ir/orbis/perf').subscribe();
		this.httpService.get<any>('https://api-mts.orbis.easytrader.ir/orbis/perfWAF').subscribe();
		this.httpService.get<any>('https://api-mts.orbis.easytrader.ir/health').subscribe();
		this.httpService.get<any>('https://api-mts.orbis.easytrader.ir/easy').subscribe();
	}

	getDeviceMenuRoot = () => `${this.deviceType == DeviceTypeEnum.Mobile ? '/' + MobileRoutes.MENU_ROUTE : ''}`;
}
